/**
 * `crypto.randomUUID` no existe en el WebView Chromium 83 — el System WebView de Android 11, que es
 * el del Scangle SP01 (verificado por CDP: `typeof crypto.randomUUID === 'undefined'` incluso en
 * secure context). Sin esto, cualquier flujo del POS que genere una clave de idempotencia
 * (abrir venta, cobrar) lanza y se queda colgado en "Guardando…".
 *
 * `randomUuid()` usa el nativo cuando existe y, si no, arma un UUID v4 con `crypto.getRandomValues`
 * (sí presente en Chromium 83). `installRandomUuidPolyfill()` además rellena `crypto.randomUUID`
 * para el código que lo llama directamente. Nunca usa `Math.random` para algo que vale como clave.
 */

type UuidString = `${string}-${string}-${string}-${string}-${string}`;

function uuidFromRandomValues(): UuidString {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Versión 4 y variante RFC 4122.
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}` as UuidString;
}

export function randomUuid(): UuidString {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return uuidFromRandomValues();
}

/** Rellena `crypto.randomUUID` en navegadores viejos, para el código que lo llama sin pasar por aquí. */
export function installRandomUuidPolyfill(): void {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID !== 'function') {
    try {
      (crypto as Crypto).randomUUID = uuidFromRandomValues;
    } catch {
      // Algunos entornos congelan `crypto`. Da igual: los llamadores propios usan `randomUuid()`.
    }
  }
}
